import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, Clock, StickyNote, MessageCircle, GraduationCap, Link2, Baby, FileText, Wallet, ShoppingBag, UserPlus, KeyRound } from "lucide-react";
import { NoteCard } from "./FeljegyzesekTab";
import { getAuthErrorMessage } from "../LoginForm";
import { fmtFt, fmtHuDate } from "./IdopontfoglalasTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const token = () => localStorage.getItem("comp_token");
const card = "rounded-3xl bg-white p-5 sm:p-6 shadow-[0_10px_40px_rgba(15,23,42,0.08)] ring-1 ring-slate-100";

const useHuClock = () => {
  const [now, setNow] = useState("");
  useEffect(() => {
    const tick = () => {
      const p = Object.fromEntries(new Intl.DateTimeFormat("hu-HU", { timeZone: "Europe/Budapest", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).formatToParts(new Date()).map((x) => [x.type, x.value]));
      setNow(`${p.year}. ${p.month}. ${p.day}. ${p.hour}:${p.minute}:${p.second}`);
    };
    tick(); const id = setInterval(tick, 1000); return () => clearInterval(id);
  }, []);
  return now;
};

const Header = ({ user }) => {
  const now = useHuClock();
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl" data-testid="home-greeting">Üdvözöljük, {user?.name}!</h1>
      <span className="font-mono text-sm text-black sm:text-base" data-testid="home-clock">{now}</span>
    </div>
  );
};

const Card = ({ icon: Icon, title, children, testId, onClick, accent = "bg-slate-100 text-slate-600", right }) => (
  <section className={`${card} ${onClick ? "cursor-pointer transition-shadow hover:shadow-xl" : ""}`} onClick={onClick} data-testid={testId}>
    <div className="mb-3 flex items-center justify-between gap-2"><div className="flex items-center gap-2"><span className={`flex h-9 w-9 items-center justify-center rounded-full ${accent}`}><Icon size={18} /></span><h2 className="text-base font-bold text-slate-900">{title}</h2></div>{right}</div>
    {children}
  </section>
);
const Soon = () => <p className="py-4 text-center text-sm text-slate-600">Hamarosan</p>;

export function KezdolapTab({ user, onNavigate }) {
  const [notes, setNotes] = useState([]);
  const [ov, setOv] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [pay, setPay] = useState({ items: [], total: 0 });
  const [busy, setBusy] = useState("");
  const P = { params: { token: token() } };
  const loadUser = () => {
    axios.get(`${API}/ugyfelkapu/trainings/my-bookings`, P).then((r) => setBookings(r.data.bookings || [])).catch(() => {});
    axios.get(`${API}/ugyfelkapu/payables`, P).then((r) => setPay(r.data)).catch(() => {});
  };
  useEffect(() => {
    if (user?.is_admin || user?.is_trainer) axios.get(`${API}/ugyfelkapu/admin/overview`, P).then((r) => setOv(r.data)).catch(() => setOv({ users: 0, horses: 0, pending: {} }));
    else { axios.get(`${API}/ugyfelkapu/notes`, P).then((r) => setNotes(r.data || [])).catch(() => {}); loadUser(); }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  const cancel = async (b) => {
    if (!window.confirm("Törlöd ezt a jelentkezést?")) return;
    setBusy(b.id);
    try { const r = await axios.post(`${API}/ugyfelkapu/trainings/bookings/${b.id}/cancel`, {}, P); toast.success(r.data.message); loadUser(); }
    catch (e) { toast.error(getAuthErrorMessage(e)); }
    setBusy("");
  };

  if (user?.is_admin || user?.is_trainer) {
    const cats = [
      ...(user.is_admin ? [["registrations", "Felhasználói regisztrációk", UserPlus, (r) => `${r.name} · ${r.email}`, ["Kérelmek", "registrations"]]] : []),
      ["link", "Hozzárendelési kérelmek", Link2, (r) => `${r.requester_name} → ${r.target_name} (${r.target_identifier})`, ["Kérelmek", "link"]],
      ["child", "Gyerekfiók kérelmek", Baby, (r) => `${r.requester_name}: ${r.child_name} (${r.child_birth_date})`, ["Kérelmek", "child"]],
      ["hianyzas", "Hiányzásigazolási kérelmek", FileText, (r) => `${r.user_name} · ${r.type} · ${r.date}`, ["Kérelmek", "hianyzas"]],
      ["rentals", "Bérlési kérelmek", KeyRound, (r) => `${r.user_name} – ${r.horse_name} · ${fmtHuDate(r.start_date)}`, ["Bérlések kezelése"]],
      ["igenyles", "Igénylések", ShoppingBag, (r) => `${r.user_name} · ${fmtFt(r.total)} · ${r.items.map((l) => `${l.name} ×${l.qty}`).join(", ")}`, ["Kérelmek", "igenyles"]],
    ];
    const p = ov?.pending || {};
    return (
      <div className="mx-auto w-full max-w-5xl space-y-6" data-testid={user.is_admin ? "kezdolap-admin" : "kezdolap-trainer"}>
        <Header user={user} />
        {user.is_admin && <div className="grid gap-4 sm:grid-cols-2">
          <Card icon={Users} title="Aktuális felhasználók" testId="home-users-card" onClick={() => onNavigate("Felhasználók")} accent="bg-sky-100 text-sky-700">
            <p className="text-4xl font-bold text-slate-900" data-testid="home-users-count">{ov ? ov.users : "…"}</p><p className="text-xs text-sky-700">Ugrás a Felhasználók részhez →</p>
          </Card>
          <Card icon={Clock} title="Aktuális lovak" testId="home-horses-card" onClick={() => onNavigate("Lovak")} accent="bg-amber-100 text-amber-700">
            <p className="text-4xl font-bold text-slate-900" data-testid="home-horses-count">{ov ? ov.horses : "…"}</p>
          </Card>
        </div>}
        <h2 className="text-lg font-bold text-slate-900">Kérelmek és igénylések</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cats.map(([key, title, Icon, fmt, nav]) => {
            const list = p[key] || [];
            return (
            <Card key={key} icon={Icon} title={title} testId={`home-req-${key}`} onClick={() => onNavigate(nav[0], nav[1])} accent="bg-yellow-100 text-yellow-800">
              <p className="mb-2 text-3xl font-bold text-slate-900" data-testid={`home-req-${key}-count`}>{ov ? list.length : "…"}</p>
              {list.length === 0 ? <p className="text-sm text-slate-600">Nincs elbírálásra váró kérelem</p> : (
                <ul className="space-y-1 text-sm text-slate-700">{list.slice(0, 5).map((r) => <li key={r.id} className="truncate">• {fmt(r)}</li>)}{list.length > 5 && <li className="text-xs text-slate-600">+{list.length - 5} további</li>}</ul>
              )}
            </Card>
          ); })}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6" data-testid="kezdolap-user">
      <Header user={user} />
      <div className="grid gap-4 lg:grid-cols-2">
        <Card icon={Wallet} title="Fizetések" testId="home-payments-card" accent="bg-emerald-100 text-emerald-700" right={<span className="text-sm font-bold text-slate-900" data-testid="home-payments-total">Összesen fizetendő: {fmtFt(pay.total)}</span>}>
          {pay.items.length === 0 ? <p className="py-4 text-center text-sm text-slate-600" data-testid="home-payments-empty">Nincs fizetendő tétel.</p> : (
            <ul className="divide-y divide-slate-100 text-sm" data-testid="home-payments-list">
              {pay.items.map((i) => (
                <li key={i.id} className="flex items-start justify-between gap-3 py-2" data-testid={`payable-${i.id}`}>
                  <div className="min-w-0"><p className="font-semibold text-slate-900">{i.participant_name} <span className="font-mono text-xs font-normal text-slate-700">{i.participant_identifier}</span></p><p className="text-xs text-slate-700">{i.title}</p></div>
                  <span className="shrink-0 font-semibold text-slate-900">{fmtFt(i.amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card icon={Clock} title="Időpontjaim" testId="home-appointments-card" accent="bg-sky-100 text-sky-700" onClick={() => onNavigate("Időpontfoglalás")}>
          {bookings.length === 0 ? <p className="py-4 text-center text-sm text-slate-600" data-testid="home-bookings-empty">Nincs foglalásod. Kattints az időpontfoglaláshoz.</p> : (
            <ul className="divide-y divide-slate-100 text-sm" data-testid="home-bookings-list">
              {bookings.map((b) => (
                <li key={b.id} className="flex items-start justify-between gap-3 py-2" data-testid={`home-booking-${b.id}`} onClick={(e) => e.stopPropagation()}>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900">{b.participant_name} · {b.type_name}</p>
                    <p className="text-xs text-slate-700">{fmtHuDate(b.date)} {b.start}–{b.end} · Fizetendő: {fmtFt(b.fee_due)}</p>
                    <p className="text-xs text-slate-700" data-testid={`home-booking-horse-${b.id}`}>Ló: {b.horse_name || "Kijelölésre vár"}</p>
                  </div>
                  {b.cancellable && <button type="button" disabled={busy === b.id} onClick={() => cancel(b)} className="shrink-0 rounded-lg border border-red-300 px-2.5 py-1 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60" data-testid={`home-booking-cancel-${b.id}`}>Jelentkezés törlése</button>}
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card icon={StickyNote} title="Feljegyzéseim" testId="home-notes-card" accent="bg-orange-100 text-orange-700">
          {notes.length === 0 ? <p className="py-4 text-center text-sm text-slate-600" data-testid="home-notes-empty">Nincs feljegyzésed.</p> : <div className="space-y-3" data-testid="home-notes-list">{notes.map((n) => <NoteCard key={n.id} n={n} />)}</div>}
        </Card>
        <Card icon={MessageCircle} title="Chat" testId="home-chat-card" accent="bg-emerald-100 text-emerald-700" onClick={() => onNavigate("Chat")}><p className="py-4 text-center text-sm text-slate-600">Ugrás a beszélgetésekhez →</p></Card>
        <Card icon={GraduationCap} title="Tanfolyamaim" testId="home-courses-card" accent="bg-purple-100 text-purple-700"><Soon /></Card>
      </div>
    </div>
  );
}

export default KezdolapTab;
